import { LogoLinksProps, LogoOrLinkProps } from './types';
import { useHomeCtx } from 'context/HomeContext';
import { hasUserAccess } from 'util/index';
import { User } from '@auth0/auth0-react';
import { useHistory } from 'react-router-dom';
import { ArcadiaLogoIcon, CJIconByArcadia } from 'cj-common-react';
import { useTheme } from '@chakra-ui/react';

export const getAppLogoLinks = (
  appPage: string,
  user: User | undefined
): LogoOrLinkProps[] => {
  const { featureFlags } = useHomeCtx();
  const theme = useTheme();

  const myReportsUrl = '/dataverse';
  const history = useHistory();
  const activeTextColor = featureFlags?.useNewArcadiaLogoAndColors ? '#7400F8' : theme?.colors?.blueOne;

  const navBarLogo = featureFlags?.useNewArcadiaLogoAndColors
    ? <ArcadiaLogoIcon
      width={124}
      height={30}
      cursor='pointer'
      handleClick={() => history.push('/')}
    />
    : <CJIconByArcadia
      width={170}
      height={32}
      cursor='pointer'
      handleClick={() => history.push('/')}
    />;

  const getRiskManagementNavLinks = (
    activeTabName: string
  ): Array<LogoOrLinkProps> => {
    const riskManagementLinks: Array<LogoOrLinkProps> = [{ logo: navBarLogo }];
    if (featureFlags.showNewRiskManagement) {
      if (hasUserAccess(user, 'riskManagementNational')) {
        riskManagementLinks.push({
          label: 'National',
          url: '/risk-management',
          isActive: activeTabName === 'National' ? 'activeLinkClass' : '',
          props: { $isActiveTextColor: activeTextColor }
        });
      }
      if (
        hasUserAccess(user, 'riskContractManagementYourAco') &&
        !featureFlags.hideContentForCjTrialMembers &&
        !featureFlags.hideYourDataTab
      ) {
        riskManagementLinks.push({
          label: 'Your Data',
          url: '/risk-management/your-data',
          isActive: activeTabName === 'Your Data' ? 'activeLinkClass' : '',
          props: { $isActiveTextColor: activeTextColor }
        });
      }
    } else {
      riskManagementLinks.push({
        label: 'Risk Management Home',
        url: '/risk-management',
        isActive: activeTabName === 'National' ? 'activeLinkClass' : '',
        props: { $isActiveTextColor: activeTextColor }
      });
    }
    return riskManagementLinks;
  };

  let APP_LOGO_LINKS: LogoLinksProps = {
    explorer: [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/explorer',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    explorerResults: [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/explorer',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    explorerMarket: [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/explorer',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    explorerProfiles: [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/explorer',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    explorerCohort: [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/explorer',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    explorerProvider: [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/explorer',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    'explorerOther-profiles': [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/explorer',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    home: [{ logo: navBarLogo }],
    'my-reports': [
      { logo: navBarLogo },
      {
        label: 'My Reports Home',
        url: myReportsUrl,
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    'my-reportsId': [
      { logo: navBarLogo },
      {
        label: 'My Reports Home',
        url: myReportsUrl,
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    cohort: [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/cohort',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    cohortProfile: [
      { logo: navBarLogo },
      {
        label: 'Market Intelligence Home',
        url: '/cohort',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    'provider-performance': [
      { logo: navBarLogo },
      ...(featureFlags.showNewProviderAdvantage
        ? [
            {
              label: 'Provider Profiles',
              url: '/provider-performance-v2',
              props: { $isActiveTextColor: activeTextColor }
            }
          ]
        : []),
      {
        label: featureFlags.showNewProviderAdvantage
          ? 'Dashboards'
          : 'Provider Performance Home',
        url: '/provider-performance',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    'provider-performanceDashboard': [
      { logo: navBarLogo },
      {
        label: 'Provider Performance Home',
        url: '/provider-performance',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    'care-retention': [
      { logo: navBarLogo },
      {
        label: 'Care Retention Home',
        url: '/care-retention',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    dataverse: [
      { logo: navBarLogo },
      {
        label: 'Dataverse Home',
        url: '/dataverse',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    dataverseReport: [
      { logo: navBarLogo },
      {
        label: 'Dataverse Home',
        url: '/dataverse',
        props: { $isActiveTextColor: activeTextColor }
      }
    ],
    'risk-management': getRiskManagementNavLinks('National'),
    'risk-managementProfiles': getRiskManagementNavLinks('National'),
    'risk-managementYour-data': getRiskManagementNavLinks('Your Data'),
    'risk-managementDashboard': getRiskManagementNavLinks('Your Data'),
    'care-accelerator': [
      { logo: navBarLogo },
      {
        label: 'Patient Pulse',
        url: '/care-accelerator',
        isActive: 'activeLinkClass',
        props: { $isActiveTextColor: activeTextColor }
      }
    ]
  };
  if (featureFlags.showNewProviderAdvantage) {
    APP_LOGO_LINKS = {
      ...APP_LOGO_LINKS,
      'provider-performance-v2': [
        { logo: navBarLogo },
        {
          label: 'Provider Profiles',
          url: '/provider-performance-v2',
          isActive: 'activeLinkClass',
          props: { $isActiveTextColor: activeTextColor }
        },
        ...(featureFlags.showDashboardsLink
          ? [
              {
                label: 'Dashboards',
                url: '/provider-performance',
                props: { $isActiveTextColor: activeTextColor }
              }
            ]
          : [])
      ],
      'provider-performance-v2Profiles': [
        { logo: navBarLogo },
        {
          label: 'Provider Profiles',
          url: '/provider-performance-v2',
          props: { $isActiveTextColor: activeTextColor }
        },
        ...(featureFlags.showDashboardsLink
          ? [
              {
                label: 'Dashboards',
                url: '/provider-performance',
                props: { $isActiveTextColor: activeTextColor }
              }
            ]
          : [])
      ],
      'provider-performance-v2Results': [
        { logo: navBarLogo },
        {
          label: 'Provider Profiles',
          url: '/provider-performance-v2',
          isActive: 'activeLinkClass',
          props: { $isActiveTextColor: activeTextColor }
        },
        ...(featureFlags.showDashboardsLink
          ? [
              {
                label: 'Dashboards',
                url: '/provider-performance',
                props: { $isActiveTextColor: activeTextColor }
              }
            ]
          : [])
      ],
      'provider-performance-v2Participants': [
        { logo: navBarLogo },
        {
          label: 'Provider Profiles',
          url: '/provider-performance-v2',
          isActive: 'activeLinkClass',
          props: { $isActiveTextColor: activeTextColor }
        },
        ...(featureFlags.showDashboardsLink
          ? [
              {
                label: 'Dashboards',
                url: '/provider-performance',
                props: { $isActiveTextColor: activeTextColor }
              }
            ]
          : [])
      ]
    };
  }
  return APP_LOGO_LINKS[appPage] ?? [];
};
